/**
 * Cognito authentication service — SRP-based, no Hosted UI.
 * Configurable via AuthConfig (project-specific User Pool).
 */
import { CognitoUserPool, CognitoUser, AuthenticationDetails, } from 'amazon-cognito-identity-js';
import { getAuthConfig } from '../config';
/** Get the Cognito User Pool instance for the current project */
function getUserPool() {
    const { cognito } = getAuthConfig();
    return new CognitoUserPool({
        UserPoolId: cognito.userPoolId,
        ClientId: cognito.clientId,
    });
}
/**
 * Authenticate user with username and password (SRP).
 */
export function login(username, password) {
    return new Promise((resolve) => {
        const userPool = getUserPool();
        const user = new CognitoUser({ Username: username, Pool: userPool });
        const authDetails = new AuthenticationDetails({ Username: username, Password: password });
        user.authenticateUser(authDetails, {
            onSuccess: (session) => {
                resolve({ success: true, session, user });
            },
            onFailure: (err) => {
                let message = 'Errore di autenticazione';
                if (err.code === 'NotAuthorizedException') {
                    message = 'Credenziali non valide';
                }
                else if (err.code === 'UserNotFoundException') {
                    message = 'Utente non trovato';
                }
                else if (err.code === 'UserNotConfirmedException') {
                    message = 'Account non confermato. Contatta l\'amministratore.';
                }
                else if (err.code === 'PasswordResetRequiredException') {
                    message = 'È necessario reimpostare la password.';
                }
                else if (err.code === 'TooManyRequestsException') {
                    message = 'Troppi tentativi. Riprova tra qualche minuto.';
                }
                else if (err.message) {
                    message = err.message;
                }
                resolve({ success: false, error: message });
            },
            newPasswordRequired: () => {
                resolve({ success: false, challenge: 'NEW_PASSWORD_REQUIRED', user });
            },
            totpRequired: () => {
                resolve({ success: false, challenge: 'MFA_REQUIRED', user });
            },
            mfaRequired: () => {
                resolve({ success: false, challenge: 'MFA_REQUIRED', user });
            },
        });
    });
}
/**
 * Complete "new password required" challenge.
 */
export function completeNewPassword(user, newPassword) {
    return new Promise((resolve) => {
        user.completeNewPasswordChallenge(newPassword, {}, {
            onSuccess: (session) => {
                resolve({ success: true, session, user });
            },
            onFailure: (err) => {
                let message = 'Errore durante il cambio password';
                if (err.code === 'InvalidPasswordException') {
                    message = 'La password non rispetta i requisiti: minimo 8 caratteri, una maiuscola, una minuscola, un numero e un carattere speciale.';
                }
                else if (err.message) {
                    message = err.message;
                }
                resolve({ success: false, error: message });
            },
        });
    });
}
/**
 * Submit MFA code (TOTP).
 */
export function submitMfaCode(user, code) {
    return new Promise((resolve) => {
        user.sendMFACode(code, {
            onSuccess: (session) => {
                resolve({ success: true, session, user });
            },
            onFailure: (err) => {
                resolve({ success: false, error: err.message || 'Codice MFA non valido' });
            },
        }, 'SOFTWARE_TOKEN_MFA');
    });
}
/**
 * Extract tokens from a successful session.
 */
export function getTokensFromSession(session) {
    return {
        accessToken: session.getAccessToken().getJwtToken(),
        idToken: session.getIdToken().getJwtToken(),
        refreshToken: session.getRefreshToken().getToken(),
    };
}
/**
 * Get user info from ID token payload.
 */
export function getUserInfoFromSession(session) {
    const payload = session.getIdToken().payload;
    return {
        sub: payload.sub,
        email: payload.email,
        givenName: payload.given_name || '',
        familyName: payload.family_name || '',
        groups: payload['cognito:groups'] || [],
    };
}
/**
 * Sign out current user — global sign out (invalidates all sessions on Cognito).
 */
export function logout() {
    const userPool = getUserPool();
    const user = userPool.getCurrentUser();
    if (user) {
        // Global sign out: invalidates refresh token server-side
        user.globalSignOut({
            onSuccess: () => { },
            onFailure: () => { },
        });
    }
    // Clear all Cognito tokens from localStorage
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('CognitoIdentityServiceProvider')) {
            keysToRemove.push(key);
        }
    }
    keysToRemove.forEach((key) => localStorage.removeItem(key));
    // Clear session storage (dismiss flags, etc.)
    sessionStorage.clear();
}
/**
 * Try to restore session from stored refresh token.
 */
export function getCurrentSession() {
    return new Promise((resolve) => {
        const userPool = getUserPool();
        const user = userPool.getCurrentUser();
        if (!user) {
            resolve(null);
            return;
        }
        user.getSession((err, session) => {
            if (err || !session || !session.isValid()) {
                resolve(null);
            }
            else {
                resolve(session);
            }
        });
    });
}
/**
 * Get a CognitoUser instance for forgot-password flow.
 */
export function getCognitoUser(username) {
    const userPool = getUserPool();
    return new CognitoUser({ Username: username, Pool: userPool });
}
/**
 * Associate a TOTP software token (get secret for QR code).
 * Must be called with an authenticated user.
 */
export function associateSoftwareToken() {
    return new Promise((resolve, reject) => {
        const userPool = getUserPool();
        const user = userPool.getCurrentUser();
        if (!user) {
            reject(new Error('No authenticated user'));
            return;
        }
        user.getSession((err) => {
            if (err) {
                reject(err);
                return;
            }
            user.associateSoftwareToken({
                associateSecretCode: (secretCode) => {
                    resolve({ secretCode });
                },
                onFailure: (err2) => {
                    reject(err2);
                },
            });
        });
    });
}
/**
 * Verify the TOTP software token with a code from the authenticator app.
 * Must be called after associateSoftwareToken.
 */
export function verifySoftwareToken(code, friendlyName = 'TOTP') {
    return new Promise((resolve, reject) => {
        const userPool = getUserPool();
        const user = userPool.getCurrentUser();
        if (!user) {
            reject(new Error('No authenticated user'));
            return;
        }
        user.getSession((err) => {
            if (err) {
                reject(err);
                return;
            }
            user.verifySoftwareToken(code, friendlyName, {
                onSuccess: () => {
                    resolve({ success: true });
                },
                onFailure: (err2) => {
                    reject(err2);
                },
            });
        });
    });
}
//# sourceMappingURL=cognito-auth.js.map